/**
 * Back testing's two shapes, the scenario type and the scenario, with the helpers
 * that put a type's outcome and decision paths into words. A scenario is what the
 * simulator loads as a ScenarioCase; only the field names differ, bridged below.
 */

import type { ScenarioCase } from '@/modules/simulator/session/scenario-case';
import type {
	BranchDecision,
	GeneratedScenarioType,
	ScenarioRecording,
	ScenarioTypeBranch,
	ScenarioTypeTool,
	StoredScenario
} from '@/services/ai-function.service';
import type { ScenarioKind } from '@/types/websocket.types';
import { counted } from './format';

/** An outcome of the protocol: the writes, last decision and ending that one or more decision paths reach. */
export interface ScenarioType {
	id: string;
	sopId: string;
	/** The customer's situation, one line. */
	description: string;
	/** What the protocol does about it, one line. */
	outcome: string;
	/** Tools its decision paths call, with the result expected from each. */
	tools: ScenarioTypeTool[];
	/** The outcome in the protocol's compiled graph, with the decision paths that reach it. */
	branch: ScenarioTypeBranch;
}

/** One test case: a past ticket the bucketing placed under a type or under none, or a customer thread fabricated for a type. */
export interface Scenario {
	id: string;
	sopId: string;
	kind: ScenarioKind;
	scenarioTypeId: string | null;
	/** Null on a fabricated scenario. */
	ticketId: string | null;
	/** The ticket's customer, server-attached; the scenario world's own fields take precedence. */
	customer?: { name: string | null; email: string | null } | null;
	/** The ticket's recorded AI turn; null when it has none, absent when unknown. */
	recording?: ScenarioRecording | null;
	description: string;
	query: ScenarioCase['messages'];
	toolOutput: ScenarioCase['simulationConfig'];
	/** Who fabricated it; null on a past ticket. */
	createdBy: number | null;
	createdAt?: string;
	/** Why the bucketing put the ticket here. */
	why?: string;
}

/** The most scenarios one bulk run takes, matching the backend's MAX_BULK_ITEMS; also the selection cap. */
export const BULK_RUN_MAX_ITEMS = 20;

const ENDING_PHRASES: Record<ScenarioTypeBranch['ending']['kind'], string> = {
	reply: 'a reply',
	generated_reply: 'a generated reply',
	escalation: 'an escalation',
	other: 'a final step'
};

/** Where an outcome ends, as a phrase: "a reply", "an escalation", "a fallback escalation". */
export const endingPhrase = (branch: ScenarioTypeBranch): string =>
	branch.fallback ? `a fallback ${branch.ending.kind === 'escalation' ? 'escalation' : 'step'}` : ENDING_PHRASES[branch.ending.kind];

/** The writes the type's decision paths make on the way to its ending. */
export const writesOf = (branch: ScenarioTypeBranch): string[] => [
	...new Set(branch.tools.filter((tool) => tool.write).map((tool) => tool.tool))
];

/** A type's outcome in one line: its one decision path's decisions and tools, or its path count and writes; then where it ends. */
export const branchTrail = (branch: ScenarioTypeBranch): string => {
	const paths = branch.decisionPathCount;
	const decisions = branch.decisions.length;
	const writes = writesOf(branch);
	const steps =
		paths > 1
			? [`${paths} paths`, writes.length ? `writes ${writes.join(', ')}` : 'no writes']
			: [
					decisions ? counted(decisions, 'decision') : null,
					branch.tools.length ? branch.tools.map((tool) => tool.tool).join(', ') : null
				];
	return [...steps, `ends in ${endingPhrase(branch)}`].filter((part): part is string => part !== null).join(' · ');
};

type PathStep =
	| { kind: 'decision'; nodeId: string; label: string; condition: string }
	| { kind: 'tool'; nodeId: string; tool: string; label: string };

/** A decision path's decisions and tool calls in the order it takes them; its ending comes after them all. */
export const pathSteps = (branch: ScenarioTypeBranch): PathStep[] => {
	const at = (nodeId: string) => branch.nodeIds.indexOf(nodeId);
	return [
		...branch.decisions.map((decision) => ({ kind: 'decision' as const, ...decision })),
		...branch.tools.map(({ nodeId, tool, label }) => ({ kind: 'tool' as const, nodeId, tool, label }))
	].sort((a, b) => at(a.nodeId) - at(b.nodeId));
};

/** A decision path's decisions in words, in the order it takes them. */
export const decisionChain = (decisions: BranchDecision[]): string =>
	decisions.length
		? decisions
				.map(({ label, condition }) =>
					condition === 'otherwise' ? 'none of the cases above' : label ? `${label}: ${condition}` : condition
				)
				.join(' → ')
		: 'no decision: every customer of this protocol comes this way';

/** One decision path of a type's outcome, by position, shaped as its branch. */
export const pathOf = (branch: ScenarioTypeBranch, index: number): ScenarioTypeBranch => {
	const path = branch.decisionPaths[index];
	return path ? { ...branch, nodeIds: path.nodeIds, decisions: path.decisions, tools: path.tools } : branch;
};

/** Server rows carry their ids; only the local sopId linkage is added here. */
export const toLocalType = (sopId: string, t: GeneratedScenarioType & { id: string }): ScenarioType => ({
	id: t.id,
	sopId,
	description: t.description,
	outcome: t.outcome,
	tools: t.tools,
	branch: t.branch
});

/** The server's row, carrying the SOP it was asked for; the wire omits it as implied. */
export const toLocalScenario = (sopId: string, s: StoredScenario): Scenario => ({
	id: s.id,
	sopId,
	kind: s.kind,
	scenarioTypeId: s.scenarioTypeId,
	ticketId: s.ticketId,
	customer: s.customer ?? null,
	recording: s.recording,
	description: s.description,
	query: s.query,
	toolOutput: s.toolOutput,
	createdBy: s.createdBy,
	createdAt: s.createdAt,
	why: s.why
});

/** The simulator's own shape, produced when a scenario is opened in the simulator. */
export const toScenarioCase = (scenario: Scenario): ScenarioCase => ({
	id: scenario.id,
	name: scenario.description,
	messages: scenario.query,
	simulationConfig: scenario.toolOutput,
	ticketId: scenario.ticketId ?? undefined,
	kind: scenario.kind,
	sopId: scenario.sopId,
	scenarioTypeId: scenario.scenarioTypeId ?? undefined
});
